define('presentation/welcome/viewModels/onboardingDeliveryViewModel',[
    'i18next',
    'businessServices/authentication/accountPlanInfo',
    'businessServices/authentication/sessionWorkflowFlags',
    'common/converters/phoneNumberFormatter',
    'common/promises/promiseFactory',
    'constants/countryEnumerations',
    'presentation/welcome/common/onboardingNavigationManager',
    'presentation/welcome/common/onboardingSectionValidationDisplay',
    'presentation/welcome/facades/onboardingDeliveryFacade',
    'presentation/welcome/validators/onboardingDeliveryValidator',
], function() {
    return function() {
        let self = this;

        const PhoneNumberFormatterConstructor = require('common/converters/phoneNumberFormatter');
        const _phoneNumberFormatter = new PhoneNumberFormatterConstructor();

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const SectionValidationDisplayConstructor = require('presentation/welcome/common/onboardingSectionValidationDisplay');
        const _sectionValidationDisplay = new SectionValidationDisplayConstructor();

        const _accountPlanInfo = require('businessServices/authentication/accountPlanInfo');
        const _countryEnumerations = require('constants/countryEnumerations');
        const _i18n = require('i18next');
        const _sessionWorkflowFlags = require('businessServices/authentication/sessionWorkflowFlags');

        let _facade = null;
        let _disposables = [];
        let _onboardingNavigationManager = null;
        let _validator = null;

        const MAX_ITEMS_CAN_ADD = 10;

        let _startTrialClicked = false;

        self.isScrollToOffsetActive = true;
        self.isCompositionComplete = ko.observable(false);
        self.showValidation = ko.observable(false);
        self.isScrollActive = ko.observable(false);

        self.headingText = _i18n.t('onboardingDelivery:headingText');
        self.subtitleText = _i18n.t('onboardingDelivery:subtitleText');
        self.showRequiredItemsWarning = ko.observable(false);

        self.messageRecipients = ko.observableArray([]);
        self.smsRecipients = ko.observableArray([]);
        self.shouldDisplaySmsNotifications = ko.observable(false);
        self.wantsOnboardingSpecialist = ko.observable(false);

        // A customer who booked before taking the message-taking upsell already has a specialist
        // lined up, so this step tells them so instead of asking again
        self.isOnboardingCallBooked = ko.pureComputed(() => {
            return _accountPlanInfo.isLiveReceptionistOnboardingScheduled();
        });

        _sectionValidationDisplay.init(self.showValidation);
        self.canDisplaySmsNotificationsValidation = _sectionValidationDisplay.createSectionDisplay(self.shouldDisplaySmsNotifications);

        self.nextPageButtonText = ko.observable(_i18n.t('onboardingFooter:done'));

        self.emailTooltip = ko.pureComputed(() => _i18n.t('onboardingDelivery:emailTooltip'));
        self.phoneNumberTooltip = ko.pureComputed(() => _i18n.t('onboardingDelivery:numberTooltip'));

        self.canAddAnotherMessageRecipient = ko.pureComputed(() => self.messageRecipients().length < MAX_ITEMS_CAN_ADD);
        self.canDeleteRecipient = ko.pureComputed(() => self.messageRecipients().length > 0);
        self.addMessageRecipientBtnText = ko.pureComputed(() => {
            if (self.canAddAnotherMessageRecipient()) {
                return _i18n.t('onboardingDelivery:addEmailAddress');
            } else {
                return _i18n.t('onboardingDelivery:disabledAddEmailAddress');
            }
        });

        self.canAddAnotherSmsRecipient = ko.pureComputed(() => self.smsRecipients().length < MAX_ITEMS_CAN_ADD);
        self.addSmsRecipientBtnText = ko.pureComputed(() => {
            if (self.canAddAnotherSmsRecipient()) {
                return _i18n.t('onboardingDelivery:addPhoneNumber');
            } else {
                return _i18n.t('onboardingDelivery:disabledAddPhoneNumber');
            }
        });

        self.isNavigateToNextPageActive = ko.pureComputed(() => {
            const areMessageRecipientsValid = self.messageRecipients().every((recipient) => recipient.emailAddress.isValid());
            const areSmsRecipientsValid = self.smsRecipients().every((recipient) => recipient.number.isValid() !== false);
            return areMessageRecipientsValid && areSmsRecipientsValid && self.showRequiredItemsWarning() === false;
        });

        self.onRemoveMessageRecipientClick = (recipientToDelete) => {
            if (self.canDeleteRecipient() === false) {
                return;
            }
            self.messageRecipients.remove(recipientToDelete);
        };

        self.onAddMessageRecipientClick = () => {
            if (self.canAddAnotherMessageRecipient() === false) {
                return;
            }
            _addDefaultMessageRecipient();
        };

        self.onRemoveSmsRecipientClick = (recipientToDelete) => {
            if (self.smsRecipients().length <= 1) {
                recipientToDelete.number('');
                recipientToDelete.number.validate();
                return;
            }
            self.smsRecipients.remove(recipientToDelete);
        };

        self.onAddSmsRecipientClick = () => {
            if (self.canAddAnotherSmsRecipient() === false) {
                return;
            }
            _addDefaultSmsRecipient();
        };

        const _handleDisplaySmsNotificationsChanged = () => {
            if (self.shouldDisplaySmsNotifications() === false) {
                self.smsRecipients([]);
                return;
            }
            if (self.smsRecipients().length === 0) {
                _addDefaultSmsRecipient();
            }
            _revalidateSmsRecipients();
        };

        const _revalidateSmsRecipients = () => {
            self.smsRecipients().forEach((recipient) => recipient.number.validate());
        };

        self.onNextPageClicked = () => {
            return _promiseFactory.defer((deferredObject) => {
                if (_startTrialClicked) {
                    deferredObject.resolve(true);
                    return;
                }
                _startTrialClicked = true;
                _validator.validateMinimumRequiredFields()
                    .fail(deferredObject.reject)
                    .done((hasAtLeastOneRequiredField) => {
                        self.showRequiredItemsWarning(!hasAtLeastOneRequiredField);
                        _validator.validate()
                            .fail(deferredObject.reject)
                            .done((isValid) => {
                                self.showValidation(true);
                                _sectionValidationDisplay.captureDisplayedSections();

                                if (!hasAtLeastOneRequiredField || !isValid) {
                                    _startTrialClicked = false;
                                    deferredObject.resolve({keepSpinning: false});
                                    return;
                                }

                                _facade.startFreeTrial()
                                    .fail(deferredObject.reject)
                                    .done(() => {
                                        _startTrialClicked = false;
                                        // Read once, here, rather than on every toggle — the in-progress
                                        // page's completion callback needs this to survive the page
                                        // transition, and nothing else on this step depends on it live.
                                        _sessionWorkflowFlags.onboardingSpecialistRequested(self.wantsOnboardingSpecialist());
                                        deferredObject.resolve({keepSpinning: false});
                                        _onboardingNavigationManager.navigateToProcessingPage();
                                    });
                            });
                    });
            });
        };

        const _handleMessageRecipientsChanged = () => {
            self.showRequiredItemsWarning(false);
            const messageRecipients = self.messageRecipients()
                .filter((recipient) => {
                    const isEmailEmpty = recipient.emailAddress().trim() === "";
                    const isEmailValid = recipient.emailAddress.isValid();
                    return !isEmailEmpty && isEmailValid;
                })
                .map((recipient) => {
                    return recipient.emailAddress();
                });
            _facade.saveMessageRecipients(messageRecipients);
        };

        const _handleSmsRecipientsChanged = () => {
            self.smsRecipients()
                .filter((recipient) => recipient.number().trim() === '')
                .forEach((recipient) => recipient.number.validate());

            const smsRecipients = self.smsRecipients()
                .filter((recipient) => {
                    const isNumberEmpty = recipient.number().trim() === "";
                    const isNumberValid = recipient.number.isValid();
                    return !isNumberEmpty && isNumberValid;
                })
                .map((recipient) => {
                    return _phoneNumberFormatter.toEOneSixFour(recipient.number(), recipient.countryAbbreviation());
                });
            _facade.saveSmsRecipients(smsRecipients);
        };

        const _addDefaultMessageRecipient = () => {
            const recipient = {
                index: null,
                emailAddress: ko.observable('').extend({ rateLimit: 500 })
            };
            self.messageRecipients.push(recipient);
            _disposables.push(recipient.emailAddress.subscribe(_handleMessageRecipientsChanged));
            _reIndexMessageRecipients();
        };

        const _createSmsRecipient = (index) => {
            const recipient = {
                index: index,
                countryAbbreviation: ko.observable(_countryEnumerations.usa),
                number: ko.observable('').extend({ rateLimit: 500 })
            };
            recipient.isEmpty = ko.pureComputed(() => recipient.number().trim() === '');
            return recipient;
        };

        const _addDefaultSmsRecipient = () => {
            const recipient = _createSmsRecipient(null);
            self.smsRecipients.push(recipient);
            _disposables.push(recipient.countryAbbreviation.subscribe(_handleSmsRecipientsChanged));
            _disposables.push(recipient.number.subscribe(_handleSmsRecipientsChanged));
            _reIndexSmsRecipients();
        };

        const _reIndexMessageRecipients = () => {
            self.messageRecipients().forEach((recipient, index) => recipient.index = index);
        };

        const _reIndexSmsRecipients = () => {
            self.smsRecipients().forEach((recipient, index) => recipient.index = index);
        };

        const _populateDeliveryForm = (onboardingSession) => {
            const { messageRecipients, smsRecipients  } = onboardingSession;

            if (messageRecipients.length === 0) {
                _addDefaultMessageRecipient();
            } else {
                messageRecipients.map((messageRecipient, index) => {
                    const recipient = {
                        index: index,
                        emailAddress: ko.observable('').extend({ rateLimit: 500 })
                    };
                    recipient.emailAddress(messageRecipient);
                    self.messageRecipients.push(recipient);
                    _disposables.push(recipient.emailAddress.subscribe(_handleMessageRecipientsChanged));
                });
            }

            if (smsRecipients.length === 0) {
                _addDefaultSmsRecipient();
            } else {
                smsRecipients.map((smsRecipient, index) => {
                    const recipient = _createSmsRecipient(index);
                    recipient.number(_phoneNumberFormatter.toNumbers(smsRecipient));
                    self.smsRecipients.push(recipient);
                    _disposables.push(recipient.countryAbbreviation.subscribe(_handleSmsRecipientsChanged));
                    _disposables.push(recipient.number.subscribe(_handleSmsRecipientsChanged));
                });
                self.shouldDisplaySmsNotifications(true);
            }
        };

        self.detached = () => {
            _onboardingNavigationManager.detached();
            _sectionValidationDisplay.detached();
            _disposables.forEach(subscription => subscription.dispose());
            _disposables = [];
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/welcome/facades/onboardingDeliveryFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ValidatorConstructor = require('presentation/welcome/validators/onboardingDeliveryValidator');
            _validator = new ValidatorConstructor();

            const OnboardingNavigationManagerConstructor = require('presentation/welcome/common/onboardingNavigationManager');
            _onboardingNavigationManager = new OnboardingNavigationManagerConstructor();
            _onboardingNavigationManager.init();

            return _initialize();
        };

        const _initialize = () => {
            _validator.registerViewModel(self, _facade);

            _promiseFactory.deferIndefinitely(deferredObject => {
                _facade.getLiveReceptionistOnboarding()
                    .fail(deferredObject.reject)
                    .done((onboardingSession) => {
                        _populateDeliveryForm(onboardingSession);
                        _disposables.push(self.messageRecipients.subscribe(_handleMessageRecipientsChanged));
                        _disposables.push(self.smsRecipients.subscribe(_handleSmsRecipientsChanged));
                        _disposables.push(self.shouldDisplaySmsNotifications.subscribe(_handleDisplaySmsNotificationsChanged));
                        deferredObject.resolve();
                    });
            });

            return _promiseFactory.wait();
        };
    };
});

